import {
  type ActingNotePart,
  actingNote,
  buildRiskWidgetSeed,
  DEFAULT_CURRENCY,
  FLOW_MAX_GOALS_DEFAULT,
  groupActingRepresentatives,
  pickT,
} from "@repo/shared";
import type {
  ClientPartyRole,
  CompleteInstanceErrorResponse,
  MissingRequirement,
  SnapshotRegenerationStatusResponse,
  SnapshotType,
  Translated,
} from "@repo/shared/types";
import {
  fsClientType,
  isFlowInstanceEditable,
  isStandaloneReturnStatus,
  screeningClientType,
  suitabilityClientType,
} from "@repo/shared/types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Loader2, RefreshCw } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { isComponentAllowedOnPageType } from "@/components/flow/flow-component-types";
import { groupAnsweringNotes } from "@/components/flow/group-answering-notes";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertMessage } from "@/components/ui/alert-message";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import {
  Dialog,
  DialogCancel,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { EmptyState } from "@/components/ui/empty-state";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useClientIdentity } from "@/components/verified-id";
import { ApiError, apiErrorCode } from "@/lib/api/_client";
import * as flowInstancesApi from "@/lib/api/flow-instances";
import {
  useActingRepresentatives,
  useClientGroupMembers,
  useClientParties,
} from "@/lib/hooks/use-client-detail";
import { useMe } from "@/lib/hooks/use-clients";
import { useFlowConfiguredAssessmentTypes } from "@/lib/hooks/use-configured-assessment-types";
import {
  flowInstanceQueryKeys,
  useDismissProcessMessage,
  useProcessState,
} from "@/lib/hooks/use-flow-instances";
import { useAnsweringSubjects } from "@/lib/hooks/use-flow-instances/use-answering-subjects";
import { useClientClassificationFlowInstanceValue } from "@/lib/hooks/use-flow-instances/use-client-classification";
import {
  flowCompletionQueryKey,
  setFlowCompletionCache,
  useFlowCompletion,
  useInvalidateFlowCompletion,
} from "@/lib/hooks/use-flow-instances/use-flow-completion";
import {
  goalSnapshotQueryKeys,
  useInstanceExpectedValueSnapshot,
  useInvalidateGoalSnapshots,
} from "@/lib/hooks/use-flow-instances/use-goal-snapshots";
import { useInvalidateAllPortfolioSnapshots } from "@/lib/hooks/use-flow-instances/use-portfolio-snapshots";
import { useProductAssessmentFlowInstanceValue } from "@/lib/hooks/use-flow-instances/use-product-assessment";
import { useQuizFlowInstanceValue } from "@/lib/hooks/use-flow-instances/use-quiz";
import {
  useInstanceRiskAssessmentValue,
  useSubmitInstanceRiskAssessment,
} from "@/lib/hooks/use-flow-instances/use-risk-assessment";
import { useSumsubVerificationFlowInstanceValue } from "@/lib/hooks/use-flow-instances/use-sumsub-verification";
import { useTrapetsLookupFlowInstanceValue } from "@/lib/hooks/use-flow-instances/use-trapets-lookup";
import { useInvalidateFlowProcess } from "@/lib/hooks/use-flow-process-invalidation";
import { invalidateGoalPlatformsAfterRefusedSubmit } from "@/lib/hooks/use-goal-platforms";
import { useSnapshotRegenerationWatch } from "@/lib/hooks/use-snapshot-regeneration-watch";
import { useStandaloneMode } from "@/lib/hooks/use-standalone-mode";
import { useUserLanguage } from "@/lib/i18n/context";
import { FlowLanguageScope } from "@/lib/i18n/flow-language-scope";
import { useRequestFlowLanguage } from "@/lib/i18n/use-request-flow-language";
import { useT } from "@/lib/i18n/use-t";
import { log } from "@/lib/logger/browser-logger";
import { isFlowInstanceReadOnlyForUser } from "@/lib/permissions";
import type { FlowPage, FlowPageWithComponents } from "@/lib/types/flow";
import type { GoalListItem } from "@/lib/types/goal";
import { GoalListWidget } from "@/pages/flow-instances/goals/goal-list-widget";
import { MisplacedComponentNotice } from "./misplaced-component-notice";
import { resolveAttributionBanner } from "./representative-note";
import { RiskAssessmentWidget } from "./risk-assessment-widget";

// Wrappers matching { success, error } signature expected by child components
async function updateFlowInstanceName(
  instanceId: string,
  name: string,
): Promise<{ success: boolean; error?: string }> {
  try {
    await flowInstancesApi.updateName(instanceId, name);
    return { success: true };
  } catch (err) {
    return { success: false, error: err instanceof ApiError ? err.message : "Failed to save" };
  }
}

import { FlowNavigationProvider } from "@/components/flow/flow-navigation-context";
import { AdvisorNotesEditor } from "./advisor-notes-editor";
import {
  renderEmbed,
  renderFile,
  renderIframe,
  renderImage,
  renderModal,
  renderSpacer,
  renderTextBlock,
  renderYoutubeVideo,
} from "./flow-component-renderers";
import { T } from "./flow-instance-wizard.i18n";
import {
  type ClientConditionData,
  evaluateCondition,
  type FlowInstanceWizardProps,
  type GoalData,
  modelHasFieldsFor,
  WizardClientClassification,
  WizardClientPreferences,
  WizardEninLookup,
  WizardFieldRenderer,
  WizardFinancialSituation,
  WizardGoalPage,
  WizardHeader,
  WizardPageRenderer,
  WizardProductAssessment,
  WizardQuiz,
  WizardSumsubVerification,
  WizardSustainability,
  WizardTrapetsLookup,
} from "./flow-wizard";
import { AdvisorGoalContext } from "./flow-wizard/advisor-goal-context";
import { PortfolioPageRenderer } from "./flow-wizard/portfolio-page-renderer";
import { FeedbackCardList } from "./flow-wizard/process-feedback";
import { ReopenFlowDialog } from "./flow-wizard/reopen-flow-dialog";
import { InviteClientModal } from "./invite-client-modal";
import {
  AccountCashOverview,
  CashTransfers,
  GlobalAssetClassForecast,
  GlobalCostChart,
  GlobalCostTable,
  GlobalExpectedValue,
  GlobalProductCosts,
  GlobalWealthPlan,
  GoalList,
  PortfolioCosts,
  PortfolioList,
} from "./self-contained";

/** QL-493 — one line per `config_error` reason; unknown reasons fall back to the title. */
function configErrorText(t: Translated<typeof T>, reason: string): string {
  switch (reason) {
    case "volatility_chart_not_placed":
      return t.configErrorVolatilityChart;
    case "equity_share_chart_not_placed":
      return t.configErrorEquityShareChart;
    case "mandate_not_placed":
      return t.configErrorMandate;
    case "risk_assessment_not_placed":
      return t.configErrorRiskAssessment;
    case "risk_bounds_collection_setting_off":
      return t.configErrorCollectionSetting;
    default:
      return t.templateCannotComplete;
  }
}

export function FlowInstanceWizard({
  instance,
  clientId,
  productTypes = [],
  portfolioCosts = [],
  chartColors,
  refreshInstance,
}: FlowInstanceWizardProps) {
  const navigate = useNavigate();
  // QL-33 — keep standalone params when moving between the flow and process pages.
  // QL-264 / QL-263 — a standalone submit that runs the process on to a
  // hand-off step (review or completed) returns the advisor to the
  // partner system (internalRedirectUrl) instead of /process; so does a
  // cancel (QL-737).
  const { standalone, autoContinueUrl, withStandaloneParams } = useStandaloneMode();
  const [redirectingTo, setRedirectingTo] = useState<string | null>(null);
  const { userLanguage } = useUserLanguage();
  const t = useT(T);

  // Helper: refresh TanStack Query cache
  const refresh = () => {
    refreshInstance?.();
  };

  // Local UI state
  const queryClient = useQueryClient();
  // QL-399 — stage, outstanding feedback and reopen-eligibility. Deliberately
  // not part of the page's loading gate: the flow must render without it, and
  // a failed read shows no stage helper and no cards rather than a confident
  // "nothing outstanding".
  const { processState } = useProcessState(instance.id);
  const dismissMessage = useDismissProcessMessage(instance.id, (message) =>
    showAlert(t.error, message, "destructive"),
  );
  // Shared with the process page so the two cannot drift (QL-399 review).
  const invalidateProcess = useInvalidateFlowProcess(instance.id, clientId);
  const invalidateCompletion = useInvalidateFlowCompletion();
  const invalidateAllPortfolioSnapshots = useInvalidateAllPortfolioSnapshots();
  const { calculatedData: classificationValue } = useClientClassificationFlowInstanceValue(
    instance.id,
  );
  const { calculatedData: trapetsLookupValue } = useTrapetsLookupFlowInstanceValue(instance.id);
  const { calculatedData: sumsubVerificationValue } = useSumsubVerificationFlowInstanceValue(
    instance.id,
  );
  // QL-13 — quiz results feed `__quiz_result_<id>` conditional rules.
  const { calculatedData: quizValue } = useQuizFlowInstanceValue(instance.id);
  // QL-361 — product-assessment outcomes feed `__pa_…` conditional rules.
  const { calculatedData: productAssessmentValue } = useProductAssessmentFlowInstanceValue(
    instance.id,
  );
  const { configuredAssessmentTypes } = useFlowConfiguredAssessmentTypes(instance.flowId);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  // "Cancel advice" reason — whether the field shows / is mandatory comes from the flow setting.
  const cancelReasonMode = instance.flow.cancelReasonMode ?? "hidden";
  const [cancelReason, setCancelReason] = useState("");
  // Follow-up A — flips true on a 422 from /complete; auto-clears when missing.length === 0
  // (D14). Drives all error-highlighting surfaces (tab markers, widget borders, field borders).
  const [hasAttemptedIncompleteSubmit, setHasAttemptedIncompleteSubmit] = useState(false);
  // Keyed by portfolioId (QP-150 — several custom portfolios can exist per goal).
  // Fed by CustomPortfolioBuilder via onSuitabilityBlockerChange when the
  // component's `onlyAllowSuitableProducts` gate is on; blocks Submit below.
  const [suitabilityBlockers, setSuitabilityBlockers] = useState<Record<string, boolean>>({});
  const [dialogs, setDialogs] = useState<{
    confirmSubmit: boolean;
    confirmCancelAdvice: boolean;
    confirmReopen: boolean;
    deleteGoal: string | null;
    alert: { title: string; description: string; variant?: "default" | "destructive" } | null;
  }>({
    confirmSubmit: false,
    confirmCancelAdvice: false,
    confirmReopen: false,
    deleteGoal: null,
    alert: null,
  });

  // Goals from instance props (assembled from React Query in wrapper)
  const goals = instance.goals || [];
  const hasSuitabilityBlockers = Object.values(suitabilityBlockers).some((b) => b);

  const handleSuitabilityBlockerChange = useCallback((key: string, hasBlocker: boolean) => {
    setSuitabilityBlockers((prev) =>
      prev[key] === hasBlocker ? prev : { ...prev, [key]: hasBlocker },
    );
  }, []);

  // Mutation for flow completion
  const completeFlowMutation = useMutation({
    mutationFn: () => flowInstancesApi.complete(instance.id),
    onSuccess: (result) => {
      // Invalidate the whole instance subtree (status, metadata, process state, …),
      // not just metadata: the process page reads ["flowInstances", id] and
      // ["flowInstances", id, "process"], which a metadata-only key doesn't match.
      // Without this the process page renders stale "not submitted yet" until a
      // manual refresh (matches the cancel/reopen mutations below).
      // Completing a flow also moves the compliance Flows review and the
      // client's screening standing (QL-21), neither of which lives under the
      // instance key — `invalidateProcess` covers both, and documents why.
      invalidateProcess();
      // QL-264 / QL-263 — the submit response reports the resulting process
      // status. When the flow auto-generated its PDF and ran on to a hand-off
      // step (STANDALONE_RETURN_STATUSES: compliance / external review or
      // completed — all of which proceed in the background), a
      // standalone-launched advisor goes straight back to the partner system;
      // a failed PDF, a resting `submitted` / `pdf_generated`, or `signing`
      // (the advisor creates the signature order there) lands on /process as
      // usual, which shows the error / the manual actions.
      // Only the return target persisted on the instance is followed without
      // a click (see `autoContinueUrl`); a link-only param gets the Continue
      // button on the process page instead.
      if (
        standalone &&
        autoContinueUrl &&
        result?.status &&
        isStandaloneReturnStatus(result.status)
      ) {
        setRedirectingTo(autoContinueUrl);
        window.location.assign(autoContinueUrl);
        return;
      }
      // Submission opens the post-submission process page. The navigation state
      // tells it the submit just happened, so an auto-completing process that
      // finishes a moment later still returns the advisor (see
      // useStandaloneAutoContinue).
      navigate(withStandaloneParams(`/clients/${clientId}/flows/${instance.id}/process`), {
        state: { standaloneSubmitted: standalone },
      });
    },
    onError: (err) => {
      // QL-546 — the submit may have re-synced goal platforms before refusing.
      if (
        (err instanceof ApiError && err.status === 422) ||
        apiErrorCode(err) === "snapshots_regenerating"
      ) {
        invalidateGoalPlatformsAfterRefusedSubmit(queryClient, {
          isPublic: false,
          flowInstanceId: instance.id,
        });
      }
      // 422 — backend completion gate rejected (engine in apps/api). Body carries the latest
      // FlowRequirements; hydrate the completion cache so the progress bar reflects server
      // truth immediately, then surface the human message.
      if (err instanceof ApiError && err.status === 422) {
        const body = err.body as CompleteInstanceErrorResponse | undefined;
        if (body?.requirements) {
          setFlowCompletionCache(queryClient, instance.id, body.requirements);
        }
        // Follow-up A (D14) — turn on highlighting so tab/widget/field markers appear.
        // No banner: error highlighting alone communicates what's missing.
        setHasAttemptedIncompleteSubmit(true);
        setDialog("confirmSubmit", false);
        return;
      }
      // 409 — snapshot freshness. QL-675 — the refusal queued a rebuild:
      // follow it (the notice shows and the watch starts).
      if (apiErrorCode(err) === "snapshots_regenerating") {
        setDialog("confirmSubmit", false);
        showAlert(t.error, err.message, "destructive");
        applySnapshotState({ needsSnapshotRegeneration: true, snapshotsRegenerating: true });
        return;
      }
      // Anything else: show what the server actually said when it said
      // something. A refusal the backend took the trouble to explain — QL-21's
      // compliance block names the reviewer and their reason — is useless as
      // "Failed to complete flow". Matches cancelAdviceMutation below.
      setDialog("confirmSubmit", false);
      showAlert(
        t.error,
        err instanceof ApiError && err.message ? err.message : t.failedToCompleteFlow,
        "destructive",
      );
    },
  });

  const isCompleting = completeFlowMutation.isPending;

  // "Cancel advice": draft -> cancelled (read-only, PDF still possible).
  const cancelAdviceMutation = useMutation({
    mutationFn: (reason: string | null) => flowInstancesApi.processCancel(instance.id, reason),
    onSuccess: () => {
      invalidateProcess();
      // QL-737 — a standalone-launched advisor who cancels is handed back to
      // the partner system right away, like a submit that reaches a hand-off
      // step (completeFlowMutation above). Only the return target persisted on
      // the instance is followed without a click (`autoContinueUrl`); a
      // link-only param lands on /process, whose cancelled card offers the
      // Continue button instead. No countdown: a cancel is the advisor's own
      // decision, not a background step to read up on.
      if (standalone && autoContinueUrl) {
        setRedirectingTo(autoContinueUrl);
        window.location.assign(autoContinueUrl);
        return;
      }
      navigate(withStandaloneParams(`/clients/${clientId}/flows/${instance.id}/process`));
    },
    onError: (err) => {
      showAlert(
        t.error,
        err instanceof ApiError ? err.message : t.failedToCancelAdvice,
        "destructive",
      );
    },
  });

  /**
   * QL-399 — "Reopen flow" for both paths. A cancelled flow never entered the
   * process and takes the short `reopen`; anything else unwinds the process it
   * reached via `draft`. The service decides which, so the two can't drift.
   *
   * On failure nothing has been changed server-side, so the error is surfaced
   * in the open dialog and the current view, cards and count are left exactly
   * as they were to retry from.
   */
  const [reopenError, setReopenError] = useState<string | null>(null);
  const reopenMutation = useMutation({
    mutationFn: async (reason: string | null) => {
      // The two paths report different shapes; neither is used by the caller,
      // which only refetches.
      if (processState?.reopen.mode === "reopen") {
        await flowInstancesApi.processReopen(instance.id, reason);
        return;
      }
      await flowInstancesApi.processDraft(instance.id, reason);
    },
    onSuccess: () => {
      setReopenError(null);
      setDialog("confirmReopen", false);
      invalidateProcess();
    },
    onError: (err) => {
      setReopenError(err instanceof ApiError ? err.message : t.failedToReopen);
      // A signature request was being prepared (or was just created): the dialog preview
      // listed the old state, so refetch it before the retry.
      if (apiErrorCode(err) === "signature_creation_in_progress") invalidateProcess();
    },
  });

  // QL-675 — set the metadata's copy of the snapshot state from a response
  // that carries it (the Regenerate POST, the status poll, a refused submit).
  // Never by refetching the metadata: that read queues a rebuild of a flagged
  // instance, so after a failed rebuild it would queue the failing one again
  // and keep the "updating" notice up for good.
  const applySnapshotState = (state: {
    needsSnapshotRegeneration: boolean;
    snapshotsRegenerating: boolean;
  }) => {
    queryClient.setQueryData(
      flowInstanceQueryKeys.metadata(instance.id),
      (prev: Record<string, unknown> | undefined) =>
        prev
          ? {
              ...prev,
              needsSnapshotRegeneration: state.needsSnapshotRegeneration,
              snapshotsRegenerating: state.snapshotsRegenerating,
            }
          : prev,
    );
  };

  // QL-675 — what a snapshot rebuild rewrote, refreshed when the worker
  // finishes it (`useSnapshotRegenerationWatch` below).
  const invalidateRegeneratedSnapshots = (status: SnapshotRegenerationStatusResponse) => {
    applySnapshotState(status);
    // Everything under the instance, except the metadata (set above, and its
    // read queues a rebuild) and the status poll that just ended.
    queryClient.invalidateQueries({
      queryKey: flowInstanceQueryKeys.instance(instance.id),
      predicate: ({ queryKey: k }) => k[2] !== "metadata" && k[2] !== "snapshot-status",
    });
    // Invalidate every goal-snapshot subtree for goals in this instance. The instance-
    // wide rebuild doesn't return per-goal refresh hints, so we can't go
    // through `useInvalidateGoalSnapshots` — invalidate per goal at the snapshot
    // root key, which matches all 7 snapshot types under that goal. Without this,
    // the regen rewrites DB rows but `useGoalSnapshot` queries keep serving stale
    // cache (typically the pre-regen `null` data state), so widgets continue showing
    // the missing-snapshot placeholder even though the row now exists. The banner
    // dismissed correctly (because `/completion` was invalidated below) which made
    // the bug look like just a widget issue.
    for (const g of goals) {
      queryClient.invalidateQueries({ queryKey: goalSnapshotQueryKeys.allForGoal(g.id) });
    }
    // D36 (Phase 2.5) — instance-scoped snapshot keys are siblings of the
    // per-goal subtree and aren't covered by the loop above. Invalidate them
    // explicitly so GlobalCostTable / GlobalCostChart pick up the new
    // `instance_cost` row. instanceExpectedValue is invalidated too even
    // though no widget validates it today — future-proofs since the rebuild
    // produces both instance-scoped rows.
    queryClient.invalidateQueries({
      queryKey: goalSnapshotQueryKeys.instanceCost(instance.id),
    });
    queryClient.invalidateQueries({
      queryKey: goalSnapshotQueryKeys.instanceExpectedValue(instance.id),
    });
    // QL-315 — the asset-class forecast is regenerated whole-instance on the
    // same path, and its instance row is another sibling of the per-goal
    // subtree. (The per-goal rows are covered by the loop above.)
    queryClient.invalidateQueries({
      queryKey: goalSnapshotQueryKeys.instanceAssetClassForecast(instance.id),
    });
    // Snapshots are portfolio-keyed (QP-150): the goal loop above doesn't reach them.
    invalidateAllPortfolioSnapshots();
    // Required by the extended banner trigger (revised D26): when the banner fires
    // because the engine reported `kind: "snapshot"` missing entries, the rebuild's
    // end must re-check `/completion` so the banner can dismiss itself. Also
    // closes the long-standing §12.B gap #7 for the existing
    // `needsSnapshotRegeneration` path.
    invalidateCompletion(instance.id);
  };

  // QL-675 — a stale instance rebuilds in the API's worker (queued by opening
  // it, the banner button, a heal or a refused submit). Follow it, and refresh
  // every snapshot view once it ends.
  const regenerationWatch = useSnapshotRegenerationWatch({
    queryKey: flowInstanceQueryKeys.snapshotStatus(instance.id),
    regenerating: instance.snapshotsRegenerating,
    fetchStatus: () => flowInstancesApi.getSnapshotStatus(instance.id),
    onFinished: invalidateRegeneratedSnapshots,
  });
  // The watch outlives a metadata refetch that already says "done", so the
  // notice stays until the poll has refreshed the views.
  const snapshotsRegenerating = instance.snapshotsRegenerating || regenerationWatch.watching;

  // Banner button: queues the rebuild and returns at once. Its response starts
  // the watch, so even a rebuild that ends before any refetch is followed.
  const regenerateSnapshotsMutation = useMutation({
    mutationFn: () => flowInstancesApi.regenerateSnapshots(instance.id),
    onSuccess: (result) => {
      applySnapshotState(result);
    },
    onError: (err) => {
      setDialogs((d) => ({
        ...d,
        alert: {
          title: t.error,
          description: err instanceof ApiError ? err.message : t.snapshotsRefreshFailed,
          variant: "destructive",
        },
      }));
    },
  });

  // Locked once the instance leaves the editable states (draft / in_progress) —
  // i.e. submitted, in the post-submission process, completed or cancelled.
  // Uses the shared guard so the lock matches the backend write guards exactly.
  const isCompleted = !isFlowInstanceEditable(instance.status);
  // Compliance reviewers without advisor permission open the instance read-only
  // regardless of status. The backend enforces the same rule on every write.
  const { me } = useMe();
  const isReadOnly = isFlowInstanceReadOnlyForUser(me?.permissions ?? []);
  // Inputs/widgets are locked when the instance is non-editable OR the viewer is
  // read-only; mutating actions (submit/cancel/reopen/invite/rename) are hidden
  // for read-only viewers via the `isReadOnly` flag passed to the header.
  const isLocked = isCompleted || isReadOnly;
  const flowLanguage = instance.flow.language || userLanguage;
  // API errors inside the wizard come back in the flow language too.
  useRequestFlowLanguage(flowLanguage);

  // Check if portfolio value ever goes below 0 (withdrawals deplete the portfolio)
  const { data: instanceExpectedValueData } = useInstanceExpectedValueSnapshot(instance.id);
  const hasWithdrawalExceedsValue = useMemo(() => {
    const forecastData = instanceExpectedValueData as {
      forecast?: Array<{ value: string }>;
    } | null;
    if (!forecastData?.forecast) return false;
    return forecastData.forecast.some((entry) => parseFloat(entry.value || "0") < 0);
  }, [instanceExpectedValueData]);

  // Prepare client data for conditional visibility evaluation
  const clientConditionData = useMemo<ClientConditionData | undefined>(() => {
    if (!instance.client) return undefined;
    return {
      type: instance.client.type,
      // ISO code, the value a `__client_country__` rule stores (QL-323).
      country: instance.client.country ?? null,
      customValues: instance.client.customValues,
    };
  }, [instance.client]);

  // Can invite if not completed, has client data, and has public pages
  const hasPublicPages = instance.flow.pages.some((page) => page.visibility === "public");
  const canInvite = !isCompleted && !isReadOnly && instance.client && hasPublicPages;

  // Build condition evaluation data directly from React Query data
  const conditionData = useMemo(
    () => ({
      fieldValues: ((instance.values || []) as any[]).map((v: any) => ({
        flowFieldId: v.flowFieldId,
        value: v.value,
        goalId: v.goalId || null,
        portfolioId: v.portfolioId || null,
      })),
      classificationValue,
      trapetsLookupValue,
      sumsubVerificationValue,
      quizValue,
      productAssessmentValue,
      configuredAssessmentTypes,
      goals: goals.map((g: any) => ({
        id: g.id,
        // QPBI-321 — portfolioType/scheduleType are per-portfolio (QP-150); the
        // evaluator resolves them contextually (current portfolio, else first).
        portfolios: (g.portfolios ?? []).map((p: any) => ({
          id: p.id,
          portfolioType: p.portfolioType ?? "",
          scheduleType: p.scheduleType ?? "",
          // QL-774 — `__followup_mode__` reads the server-derived lineage flag.
          isFollowup: !!p.isFollowup,
        })),
        riskScore: g.riskScore,
        hasRiskAssessment: g.hasRiskAssessment,
        // QL-120 — `__goal_type__` rules resolve against the goal's stored type.
        goalType: g.goalType ?? "",
        // QL-71 — `__goal_risk_score_capped__` rules resolve against the server-derived state.
        riskScoreCapState: g.riskScoreCapState ?? "",
      })),
      clientData: clientConditionData,
      // QPBI-395 — field id → type, so rules compare values the way controls
      // render them. Client custom fields are keyed by their full
      // `__custom_field_<id>` sentinel; virtual sentinel sources are absent
      // and stay lenient.
      fieldTypes: Object.fromEntries([
        ...(instance.flow.fields ?? []).map((f) => [f.id, f.type as string]),
        ...(clientConditionData?.customValues ?? [])
          .filter((cv) => cv.customField)
          .map((cv) => [`__custom_field_${cv.customField?.id}`, cv.customField?.type as string]),
      ]),
    }),
    [
      instance.values,
      classificationValue,
      trapetsLookupValue,
      sumsubVerificationValue,
      quizValue,
      productAssessmentValue,
      configuredAssessmentTypes,
      goals,
      clientConditionData,
      instance.flow.fields,
    ],
  );

  // Split global pages by position (for multi-goal flows)
  const globalPagesBefore = useMemo(() => {
    return instance.flow.pages
      .filter(
        (page) =>
          page.visibility === "internal" &&
          page.pageType === "global" &&
          page.globalPosition === "before_goals" &&
          evaluateCondition(page.conditionalVisibility, conditionData),
      )
      .sort((a, b) => a.order - b.order);
  }, [instance.flow.pages, conditionData]);

  const globalPagesAfter = useMemo(() => {
    return instance.flow.pages
      .filter(
        (page) =>
          page.visibility === "internal" &&
          page.pageType === "global" &&
          page.globalPosition === "after_goals" &&
          evaluateCondition(page.conditionalVisibility, conditionData),
      )
      .sort((a, b) => a.order - b.order);
  }, [instance.flow.pages, conditionData]);

  // Get goal pages. QL-117 — both positions (before / after the portfolios) travel in one list;
  // the shared goal-context view splits them around the portfolio dimension.
  const goalPages = useMemo(() => {
    return instance.flow.pages
      .filter((page) => page.visibility === "internal" && page.pageType === "goal")
      .sort((a, b) => a.order - b.order);
  }, [instance.flow.pages]);

  // QP-150 — portfolio pages render inside each goal's context, once per portfolio of that goal.
  // A flow may have multiple portfolio pages.
  const portfolioPages = useMemo(() => {
    return instance.flow.pages
      .filter((page) => page.visibility === "internal" && page.pageType === "portfolio")
      .sort((a, b) => a.order - b.order);
  }, [instance.flow.pages]);

  // QL-120 — goal and portfolio page visibility is a function of the goal (and, for portfolio
  // pages, the goal × portfolio pair) rather than one context-free verdict shared by every goal.
  // The predicates travel down to the layer that knows the active goal / portfolio; global pages
  // are still condition-filtered by their own memos above, where no goal exists by design.
  const isGoalPageVisible = useCallback(
    (page: FlowPage, goalId: string) =>
      evaluateCondition(page.conditionalVisibility, { ...conditionData, goalId }),
    [conditionData],
  );
  const isPortfolioPageVisible = useCallback(
    (page: FlowPage, goalId: string, portfolioId: string) =>
      evaluateCondition(page.conditionalVisibility, { ...conditionData, goalId, portfolioId }),
    [conditionData],
  );

  // Whether the flow has any visible internal page (across all buckets). Drives the empty-state
  // card that used to key off the old flat `internalPages` list. QL-120 — goal/portfolio pages
  // now count when they are visible for AT LEAST ONE goal (resp. goal × portfolio pair); the
  // per-goal row handles the case where a particular goal sees none of them.
  const hasInternalPages =
    globalPagesBefore.length > 0 ||
    globalPagesAfter.length > 0 ||
    (goals.length === 0
      ? // No goals means no context to evaluate the page conditions against. The flag answers
        // "does this flow have internal pages configured", not "does this instance have goals",
        // so a configured goal/portfolio page counts as present here; the branches below own the
        // no-goals state itself (loadingGoalInfo for single-goal, "add first goal" for multi).
        // Instances legitimately start with zero goals — a public self-start instance the client
        // never opened, or the single-goal auto-create below still in flight.
        goalPages.length > 0 || portfolioPages.length > 0
      : goals.some((g: { id: string }) =>
          goalPages.some((page) => isGoalPageVisible(page, g.id)),
        ) ||
        goals.some((g: { id: string; portfolios?: Array<{ id: string }> }) =>
          (g.portfolios ?? []).some((pf) =>
            portfolioPages.some((page) => isPortfolioPageVisible(page, g.id, pf.id)),
          ),
        ));

  // Fetch/create single goal for single-goal flows
  useEffect(() => {
    if (!instance.flow.multiGoal) {
      async function fetchSingleGoal() {
        try {
          // API auto-creates first goal if none exist
          const existingGoals = await flowInstancesApi.getGoals(instance.id);

          if (existingGoals.length === 0) {
            const flowLang = instance.flow.language || "en_GB";
            const flowT = pickT(T, flowLang);
            // No goalType: the service applies its default; a configured
            // collection would reject a hard-coded value.
            await flowInstancesApi.createGoal(instance.id, {
              name: flowT.investmentGoal,
              amount: 1000,
              lang: flowLang,
            });
            // Auto-create grew the engine's fan-out from 0 → 1 goal — refresh completion
            // so the progress bar reflects the new goal_input requirements.
            invalidateCompletion(instance.id);
          }
          queryClient.invalidateQueries({ queryKey: flowInstanceQueryKeys.goals(instance.id) });
        } catch (err) {
          log.error({ err, instanceId: instance.id }, "failed to fetch single goal");
        }
      }
      fetchSingleGoal();
    }
  }, [instance.id, instance.flow.multiGoal, instance.flow.language, queryClient]);

  // Progress comes from the backend completion preflight (engine in apps/api).
  // Falls back to zeroes while the first fetch is in flight. `markers` powers the error
  // highlighting follow-up (H1/H2/H3/H4) — pre-computed Sets keyed by pageId, goalId,
  // componentId, fieldId, and `layoutId:tabId`.
  const { completion, markers } = useFlowCompletion(instance.id);

  // QP-184 — Group clients: member count scales the capacity buffer floor
  // (mirrors the server's computeCapacity memberCount).
  const { members: groupMembers } = useClientGroupMembers(
    instance.client?.type === "Group" ? (instance.client?.id ?? null) : null,
  );
  const capacityMemberCount = Math.max(1, groupMembers.length);

  // QL-107 — the client's custom field values keyed by field id. Reuses the
  // payload conditional visibility already reads, so the capacity tab's
  // fixed-buffer trigger costs no extra request.
  const clientCustomValues = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    for (const cv of instance.client?.customValues ?? []) {
      if (cv.customField?.id) map[cv.customField.id] = cv.value;
    }
    return map;
  }, [instance.client]);

  // QL-212 — the client-level risk answer, for a global-page placement. Read at
  // the top so the widget has its value the moment the page renders; skipped
  // when the flow has no risk model, since the widget then never renders.
  const { inputData: instanceRiskInput, calculatedData: instanceRiskCalculated } =
    useInstanceRiskAssessmentValue(instance.id, instance.flow.riskModelId);
  const submitInstanceRiskAssessment = useSubmitInstanceRiskAssessment(instance.id, {
    saveFailed: t.failedToSaveRiskAssessment,
    matrixGap: t.riskMatrixGap,
  });

  // The widget seeds from one blob, and the score it displays comes from the same
  // row. Both follow one shared rule (`buildRiskWidgetSeed`), so the advisor and
  // the client-facing flow can never disagree about what a half-finished answer
  // looks like on reload.
  //
  // The final score lives on the instance row rather than on a goal because the
  // result block renders globally only when ALL three inputs are global — and
  // then every goal resolves the same matrix cell, so there is one client-level
  // result, not a per-goal one.
  const instanceRiskSeed = useMemo(
    () => buildRiskWidgetSeed(instanceRiskInput, instanceRiskCalculated),
    [instanceRiskInput, instanceRiskCalculated],
  );

  // QP-184 — MiFID II Art. 54(6): when representatives act on the client's
  // behalf, K&E answers are theirs. Company/Group always act via a
  // representative; a Person does when a non-expired POA/guardian party is
  // registered. QL-654: there can be several (up to MAX_CLIENT_REPRESENTATIVES);
  // the on-screen notes name ALL of them. A Group falls back to the members
  // flagged as representative when no party is registered.
  const { parties: clientParties } = useClientParties(instance.client?.id ?? null);
  // QL-654 — who has to verify their identity (2+ people: one block each).
  const { identity: clientIdentity, isLoading: clientIdentityLoading } = useClientIdentity(
    instance.client?.id ?? null,
  );
  // Active as the server judges it (QL-692 review), not re-derived here.
  const activeParties = clientParties.filter((p) => p.isActive);
  const rawClientType = instance.client?.type;
  // QL-653 — a flagged member whose own client has several representatives: THEY act
  // for the group, so the notes name them ("A (power of attorney) and B (power of
  // attorney), on behalf of Person 6"), not the member.
  const { representatives: actingRepresentatives } = useActingRepresentatives(
    rawClientType === "Group" ? (instance.client?.id ?? null) : null,
  );
  // One part per flagged member: its own name (the linked client's, like the server's
  // lines), or its representatives named together as one slot. `count` is people, not
  // parts, so a member with two POAs reads plural like the risk step's K&E note.
  const actingParts: ActingNotePart[] =
    activeParties.length > 0
      ? activeParties.map((p) => ({ kind: "name", name: p.name }))
      : rawClientType === "Group"
        ? groupMembers
            .filter((m) => m.isRepresentative)
            .map((m): ActingNotePart => {
              const memberName = m.linkedClient?.name || m.name;
              const via = actingRepresentatives.filter((r) => r.actingForMemberId === m.id);
              return via.length > 0
                ? {
                    kind: "slot",
                    representatives: via.map((r) => ({
                      partyId: r.id,
                      name: r.name,
                      role: r.role as ClientPartyRole,
                      memberName,
                    })),
                  }
                : { kind: "name", name: memberName };
            })
        : [];
  const actingRepresentative =
    rawClientType === "Company" || rawClientType === "Group" || activeParties.length > 0
      ? actingNote(actingParts, flowLanguage)
      : null;

  // QL-188 — the group note on the components a group answers together; the
  // server's attribution says whether a representative acts for the group.
  const { attribution, isLoading: attributionLoading } = useAnsweringSubjects(instance.id);
  const groupNotes = groupAnsweringNotes(instance.client?.type, attribution, flowLanguage);
  // The risk assessment's K&E note must agree with the group note above it: it
  // names the acting representatives (one or several), and a group answered by
  // its MEMBERS has none, so no K&E note. Other client types keep the QP-184 rule.
  const riskRepresentative =
    rawClientType === "Group"
      ? attributionLoading
        ? null
        : (groupActingRepresentatives(attribution, flowLanguage) ??
          resolveAttributionBanner(attribution ?? undefined, actingRepresentative))
      : actingRepresentative;

  // D38 — engine-side validation of `instance_expected_value`. The engine emits
  // `{ kind: "instance_snapshot", snapshotType: "instance_expected_value" }` in
  // `/completion`'s `missing[]` when the snapshot row is absent AND every goal
  // has forecast prereqs met (hasInstanceExpectedValuePrerequisites). Wizard
  // reads the signal inline — no Set marker, since this is the only instance-
  // snapshot kind today. Replaces the Option A wizard-side memo (commit
  // c9d19014) that observed the snapshot being null client-side. See §3 D38.
  const hasInstanceExpectedValueSnapshotMissing =
    completion?.missing.some(
      (m: MissingRequirement) =>
        m.kind === "instance_snapshot" && m.snapshotType === "instance_expected_value",
    ) ?? false;

  // Follow-up A (D14) — auto-clear `hasAttemptedIncompleteSubmit` once the server says
  // nothing is missing. From the user's perspective: red borders / markers disappear as
  // soon as they finish fixing everything; new errors after that point don't re-highlight
  // until the next Submit attempt.
  useEffect(() => {
    if (hasAttemptedIncompleteSubmit && completion && completion.missing.length === 0) {
      setHasAttemptedIncompleteSubmit(false);
    }
  }, [hasAttemptedIncompleteSubmit, completion]);

  /** Gating boolean for every highlighting surface (tab markers, widget borders, field borders). */
  const showValidationErrors = hasAttemptedIncompleteSubmit;

  // Dialog states
  const confirmSubmitOpen = dialogs.confirmSubmit;
  const alertOpen = dialogs.alert !== null;
  const alertConfig = dialogs.alert || { title: "", description: "", variant: "default" as const };

  // Dialog helpers
  const setDialog = <K extends keyof typeof dialogs>(key: K, value: (typeof dialogs)[K]) => {
    setDialogs((prev) => ({ ...prev, [key]: value }));
  };

  const showAlert = (
    title: string,
    description: string,
    variant: "default" | "destructive" = "default",
  ) => {
    setDialogs((prev) => ({ ...prev, alert: { title, description, variant } }));
  };

  const clearAlert = () => {
    setDialogs((prev) => ({ ...prev, alert: null }));
  };

  const handleSaveName = async (name: string) => {
    const result = await updateFlowInstanceName(instance.id, name);
    if (result.success) {
      queryClient.invalidateQueries({ queryKey: flowInstanceQueryKeys.metadata(instance.id) });
      queryClient.invalidateQueries({ queryKey: ["clients", clientId, "flowInstances"] });
      // QL-275 — the Activity tab shows feed entries under the instance name.
      queryClient.invalidateQueries({ queryKey: ["clients", clientId, "activityEvents"] });
    } else {
      log.error(
        { errorMessage: result.error, instanceId: instance.id },
        "failed to save flow instance name",
      );
    }
  };

  const handleComplete = async () => {
    // Preflight via GET /completion (single source of truth — engine in apps/api). Force a
    // fresh fetch instead of trusting the cache: a save still in flight could otherwise let
    // the confirm dialog open against stale state.
    //
    // staleTime: 0 must be passed EXPLICITLY here. The QueryClient's default staleTime is
    // 60s (see providers/query-provider.tsx), and fetchQuery uses the QueryClient defaults
    // — not the useQuery overrides set in useFlowCompletion. Without this, a fetch within
    // 60s of any prior /completion call returns cached data and never hits the network.
    try {
      const fresh = await queryClient.fetchQuery({
        queryKey: flowCompletionQueryKey(instance.id),
        queryFn: () => flowInstancesApi.getFlowCompletion(instance.id),
        staleTime: 0,
      });
      if ((fresh?.missing?.length ?? 0) > 0) {
        // Incomplete — light up tab/widget/field markers only (no banner, per product:
        // error highlighting communicates what's missing without a warning message).
        setHasAttemptedIncompleteSubmit(true);
        return;
      }
      setDialog("confirmSubmit", true);
    } catch (err) {
      log.error({ err, instanceId: instance.id }, "completion preflight failed");
      showAlert(t.error, t.failedToCompleteFlow, "destructive");
    }
  };

  const handleCompleteConfirm = async () => {
    try {
      // Complete the flow (field values already saved per-field on blur)
      completeFlowMutation.mutate();
    } catch {
      showAlert(t.error, t.failedToCompleteFlow, "destructive");
    }
  };

  // Render component by type (for global pages)
  const renderComponent = (component: any) => {
    const isDisabled = isLocked;
    const componentHasError = showValidationErrors && markers.missingComponentIds.has(component.id);

    // Guard: a component placed on a page type it is not allowed on (e.g. a
    // goal-only component on this global page) renders a generic misplaced notice
    // instead of wrong-scope data. Single source of truth: isComponentAllowedOnPageType.
    if (!isComponentAllowedOnPageType(component.componentType, "global")) {
      return (
        <MisplacedComponentNotice
          componentType={component.componentType}
          pageType="global"
          language={flowLanguage}
        />
      );
    }

    switch (component.componentType) {
      case "field":
        if (!component.flowField) {
          return <div className="text-sm text-destructive">{t.fieldNotFound}</div>;
        }
        return (
          <WizardFieldRenderer
            field={component.flowField}
            instanceId={instance.id}
            isCompleted={isLocked}
            hasValidationError={
              showValidationErrors && markers.missingFieldKeys.has(component.flowField.id)
            }
          />
        );

      case "cost_table": {
        const cfg = component.config ? JSON.parse(component.config) : {};
        return (
          <GlobalCostTable
            instanceId={instance.id}
            config={cfg}
            language={flowLanguage}
            hasValidationError={componentHasError}
          />
        );
      }

      case "cost_chart": {
        const cfg = component.config ? JSON.parse(component.config) : {};
        return (
          <GlobalCostChart
            instanceId={instance.id}
            config={cfg}
            language={flowLanguage}
            hasValidationError={componentHasError}
          />
        );
      }

      case "expected_value": {
        const cfg = component.config ? JSON.parse(component.config) : {};
        return (
          <GlobalExpectedValue
            instanceId={instance.id}
            config={cfg}
            language={flowLanguage}
            hasValidationError={componentHasError}
          />
        );
      }

      case "asset_class_forecast": {
        const cfg = component.config ? JSON.parse(component.config) : {};
        return (
          <GlobalAssetClassForecast
            instanceId={instance.id}
            config={cfg}
            language={flowLanguage}
            chartColors={chartColors}
          />
        );
      }

      case "wealth_plan": {
        const cfg = component.config ? JSON.parse(component.config) : {};
        return (
          <GlobalWealthPlan
            instanceId={instance.id}
            currency={instance.flow.currency || DEFAULT_CURRENCY}
            isDisabled={isDisabled}
            config={cfg}
          />
        );
      }

      case "portfolio_costs": {
        const cfg = component.config ? JSON.parse(component.config) : {};
        return (
          <PortfolioCosts
            portfolioCosts={portfolioCosts}
            config={cfg}
            currency={instance.flow.currency || DEFAULT_CURRENCY}
            instanceId={instance.id}
            locked={isLocked}
          />
        );
      }

      case "product_costs": {
        const cfg = component.config ? JSON.parse(component.config) : {};
        return (
          <GlobalProductCosts
            instanceId={instance.id}
            config={cfg}
            language={flowLanguage}
            locked={isLocked}
          />
        );
      }

      case "portfolio_list": {
        const cfg = component.config ? JSON.parse(component.config) : {};
        return (
          <PortfolioList
            instanceId={instance.id}
            goals={goals.map((g: any) => ({ id: g.id, name: g.name }))}
            config={cfg}
            language={flowLanguage}
          />
        );
      }

      case "goal_list": {
        // QL-281 — instance-wide goal list with add/edit/delete.
        const cfg = component.config ? JSON.parse(component.config) : {};
        return (
          <GoalList
            instanceId={instance.id}
            goalTypes={(instance.flow as any).portfolioCollection?.goalTypes ?? []}
            config={cfg}
            language={flowLanguage}
            disabled={isDisabled}
            multiGoal={!!instance.flow.multiGoal}
            groupNote={groupNotes?.goals}
            maxGoals={instance.flow.maxGoals ?? FLOW_MAX_GOALS_DEFAULT}
          />
        );
      }

      // QL-287 — instance-wide account cash + planned transfers.
      case "account_cash_overview": {
        const cfg = component.config ? JSON.parse(component.config) : {};
        return <AccountCashOverview instanceId={instance.id} config={cfg} />;
      }

      case "cash_transfers": {
        const cfg = component.config ? JSON.parse(component.config) : {};
        return <CashTransfers instanceId={instance.id} config={cfg} disabled={isDisabled} />;
      }

      case "text_block":
        return renderTextBlock(component, flowLanguage);

      case "spacer":
        return renderSpacer(component);

      case "youtube_video":
        return renderYoutubeVideo(component);
      case "iframe":
        return renderIframe(component);

      case "image":
        return renderImage(component);

      case "embed":
        return renderEmbed(component);

      case "modal":
        return renderModal(component, flowLanguage);

      case "file":
        return renderFile(component, flowLanguage);

      // QL-212 — the client-level half of the risk assessment. Which sections it
      // actually shows is decided inside the widget from the model's scopes; a
      // model that captures everything per goal renders nothing here, and the
      // widget shows the misplaced-component notice instead. Such a placement
      // can no longer be saved (the service refuses it) and flow completion
      // drops the ones that predate that guard.
      case "risk_assessment": {
        if (!instance.flow.riskModelId) {
          return <div className="text-sm text-destructive">{t.riskModelNotConfigured}</div>;
        }
        const config = component.config ? JSON.parse(component.config) : {};
        return (
          <RiskAssessmentWidget
            riskModelId={instance.flow.riskModelId}
            pageType="global"
            goalId={null}
            value={instanceRiskSeed.value}
            onChange={(value, isComplete) => {
              try {
                const riskData = JSON.parse(value);
                // QL-212 — "complete" is decided by the widget from the sections
                // this page renders, exactly as on the goal page: a global page
                // that captures only the tolerance and the questions is complete
                // without a time horizon. The SCORE is never a client-side
                // decision — the server derives it from the matrix.
                submitInstanceRiskAssessment.mutate({ data: riskData, isComplete });
              } catch (error) {
                log.error({ err: error }, "Failed to parse risk assessment data:");
              }
            }}
            disabled={isDisabled}
            style={config.style}
            title={config.title}
            description={config.description}
            hasValidationError={componentHasError}
            // A global entry carries neither goalId nor portfolioId, so
            // `deriveMarkers` keys its reason by the bare component id.
            missingReason={markers.missingComponentReasons.get(component.id)}
            finalScore={instanceRiskSeed.finalScore}
            isScoreCapped={instanceRiskSeed.isScoreCapped}
            actingRepresentative={riskRepresentative}
            groupNote={groupNotes?.riskAssessment}
          />
        );
      }

      case "client_classification": {
        const config = component.config ? JSON.parse(component.config) : {};
        return (
          <WizardClientClassification
            instanceId={instance.id}
            clientType={suitabilityClientType(instance.client?.type)}
            actingRepresentative={actingRepresentative}
            isGroupClient={instance.client?.type === "Group"}
            clientName={instance.client?.name || "Client"}
            disabled={isDisabled}
            style={config.style}
            title={config.title}
            description={config.description}
            hasValidationError={componentHasError}
          />
        );
      }

      case "product_assessment": {
        if (productTypes.length === 0 || !instance.flow.portfolioCollectionId) {
          return <EmptyState>{t.noProductTypesConfigured}</EmptyState>;
        }
        const config = component.config ? JSON.parse(component.config) : {};
        return (
          <WizardProductAssessment
            instanceId={instance.id}
            portfolioCollectionId={instance.flow.portfolioCollectionId}
            portfolioCollectionName={(instance.flow as any).portfolioCollection?.name || "Products"}
            productTypes={productTypes}
            disabled={isDisabled}
            style={config.style}
            title={config.title}
            description={config.description}
            hasValidationError={componentHasError}
          />
        );
      }

      case "sustainability": {
        const config = component.config ? JSON.parse(component.config) : {};
        return (
          <WizardSustainability
            instanceId={instance.id}
            disabled={isDisabled}
            config={config}
            sustainabilityConfig={
              (instance.flow.portfolioCollection as any)?.sustainabilityConfig ?? null
            }
            language={flowLanguage}
            hasValidationError={componentHasError}
            groupNote={groupNotes?.sustainability}
          />
        );
      }

      case "client_preferences": {
        const config = component.config ? JSON.parse(component.config) : {};
        return (
          <WizardClientPreferences
            instanceId={instance.id}
            disabled={isDisabled}
            config={config}
            preferences={(instance.flow.portfolioCollection as any)?.clientPreferences ?? []}
            language={flowLanguage}
            hasValidationError={componentHasError}
            groupNote={groupNotes?.clientPreferences}
          />
        );
      }

      case "quiz": {
        // QL-13 — the quiz is authored in this component's config, so the
        // component id is the quiz id. Only the presentation bits are read
        // here; the questions never reach the client.
        const config = component.config ? JSON.parse(component.config) : {};
        return (
          <WizardQuiz
            instanceId={instance.id}
            componentId={component.id}
            disabled={isDisabled}
            config={config}
            language={flowLanguage}
            hasValidationError={componentHasError}
          />
        );
      }

      case "financial_situation": {
        // QP-176/QL-140 — the RAW client type: a Company or Group client gets
        // the widget when the flow's model has at least one field applicable to
        // its type; otherwise the N/A message (mirrors the engine's global-N/A
        // guard, which also covers a flow with no FS model at all).
        const fsType = fsClientType(instance.client?.type);
        if (
          fsType !== "Person" &&
          !modelHasFieldsFor(instance.flow.financialSituationModel, fsType)
        ) {
          return (
            <div className="text-sm text-muted-foreground border border-dashed rounded-lg p-4">
              {t.financialSituationNotAvailable(
                fsType === "Company" ? t.clientTypeWordCompany : t.clientTypeWordGroup,
              )}
            </div>
          );
        }
        const config = component.config ? JSON.parse(component.config) : {};
        // QP-183 — the engine's failure reason drives the deposit-gate banner.
        // QL-425 — keyed by this component: a flow that splits the financial
        // situation across components gets each gap only where its tab is.
        const fsMissingReason = markers.missingComponentReasons.get(component.id);
        return (
          <WizardFinancialSituation
            instanceId={instance.id}
            disabled={isDisabled}
            config={config}
            financialSituationModel={instance.flow.financialSituationModel}
            clientType={fsType}
            clientOrgId={instance.client?.orgId}
            clientCountry={instance.client?.country}
            capacityMemberCount={capacityMemberCount}
            clientCustomValues={clientCustomValues}
            missingReason={fsMissingReason}
            flowCurrency={instance.flow.currency || DEFAULT_CURRENCY}
            language={flowLanguage}
            hasValidationError={componentHasError}
          />
        );
      }

      case "trapets_lookup": {
        const config = component.config ? JSON.parse(component.config) : {};
        return (
          // QL-21 follow-up — screening keeps Group DISTINCT.
          // `suitabilityClientType` folds Group into Person, which is right for
          // financial models and wrong here: it sent a group down the person
          // branch, where the advisor had to type a natural person's national id
          // as the group's own.
          <WizardTrapetsLookup
            instanceId={instance.id}
            clientId={instance.client?.id ?? null}
            clientType={screeningClientType(instance.client?.type)}
            clientName={instance.client?.name || null}
            clientCountry={instance.client?.country || null}
            clientSSN={instance.client?.ssn || null}
            clientDateOfBirth={instance.client?.dateOfBirth || null}
            clientOrgId={instance.client?.orgId || null}
            disabled={isDisabled}
            language={flowLanguage}
            style={config.style}
            title={config.title}
            description={config.description}
            hasValidationError={componentHasError}
          />
        );
      }

      case "enin_lookup": {
        // Company-only component — person clients render nothing.
        if (instance.client?.type !== "Company") return null;
        const config = component.config ? JSON.parse(component.config) : {};
        return (
          <WizardEninLookup
            instanceId={instance.id}
            clientName={instance.client?.name || null}
            clientCountry={instance.client?.country || null}
            clientOrgId={instance.client?.orgId || null}
            disabled={isDisabled}
            language={flowLanguage}
            style={config.style}
            title={config.title}
            description={config.description}
            hasValidationError={componentHasError}
          />
        );
      }

      case "sumsub_verification": {
        const config = component.config ? JSON.parse(component.config) : {};
        return (
          <WizardSumsubVerification
            instanceId={instance.id}
            actingRepresentative={actingRepresentative}
            subjects={clientIdentity?.summary.subjects}
            subjectsLoading={clientIdentityLoading}
            style={config.style}
            title={config.title}
            description={config.description}
            language={flowLanguage}
            componentId={component.id}
            disabled={isDisabled}
            hasValidationError={componentHasError}
          />
        );
      }

      default:
        return null;
    }
  };

  // Render a global page (for multi-goal flow)
  const renderGlobalPage = (page: any) => {
    const pageWithComponents = page as FlowPageWithComponents;
    return (
      <div className="space-y-6">
        <FlowLanguageScope language={flowLanguage}>
          <WizardPageRenderer
            layouts={pageWithComponents.layouts || []}
            components={pageWithComponents.components || []}
            conditionData={conditionData}
            renderComponent={renderComponent}
            showValidationErrors={showValidationErrors}
            missingTabKeys={markers.missingTabKeys}
          />
        </FlowLanguageScope>
        {page.enableAdvisorNotes && (
          <AdvisorNotesEditor
            flowInstanceId={instance.id}
            flowPageId={page.id}
            isEditable={!isLocked}
            isRequired={page.requireAdvisorNotes === true}
            hasValidationError={showValidationErrors && markers.missingAdvisorNoteKeys.has(page.id)}
            context={{
              flowName: instance.flow.name,
              clientName: instance.client?.name || "Client",
              pageName: page.name,
              language: flowLanguage,
            }}
          />
        )}
      </div>
    );
  };

  // Render a goal page (one page for one goal). Shared by the multi-goal nav and single-goal row.
  const renderGoalPage = (page: any, goalId: string) => (
    <WizardGoalPage
      page={page}
      goalId={goalId}
      instanceId={instance.id}
      goals={goals as unknown as GoalData[]}
      clientName={instance.client?.name || "Client"}
      conditionData={conditionData}
      isCompleted={isLocked}
      flowLanguage={flowLanguage}
      flowName={instance.flow.name}
      riskModelId={instance.flow.riskModelId}
      goalTypes={(instance.flow as any).portfolioCollection?.goalTypes ?? []}
      chartColors={chartColors}
      showValidationErrors={showValidationErrors}
      missingGoalComponentKeys={markers.missingGoalComponentKeys}
      missingFieldKeys={markers.missingFieldKeys}
      missingTabKeys={markers.missingTabKeys}
      missingAdvisorNoteKeys={markers.missingAdvisorNoteKeys}
      actingRepresentative={riskRepresentative}
      groupNotes={groupNotes}
    />
  );

  // QL-314 — when portfolio composition changes (add/update/delete products), the server
  // regenerates goal-level snapshots (assetClassForecast with volatilityPct). Thread the
  // refreshHints through onSaveComplete so useInvalidateGoalSnapshots can selectively
  // invalidate the goal-level asset class forecast + volatility chart queries.
  const invalidateGoalSnapshots = useInvalidateGoalSnapshots();
  const handlePortfolioSaveComplete = useCallback(
    (goalId: string, refreshHints?: { invalidated?: SnapshotType[]; cleared?: boolean }) => {
      invalidateGoalSnapshots(goalId, refreshHints);
    },
    [invalidateGoalSnapshots],
  );

  // Render one portfolio page for one resolved portfolio. The nav layer picks the portfolio.
  const renderPortfolioPage = (page: any, goalId: string, portfolio: any) => (
    <PortfolioPageRenderer
      page={page}
      portfolio={portfolio}
      instanceId={instance.id}
      goalId={goalId}
      goals={goals}
      isCompleted={isLocked}
      flowLanguage={flowLanguage}
      conditionData={conditionData}
      chartColors={chartColors}
      portfolioCosts={portfolioCosts}
      currency={instance.flow.currency || DEFAULT_CURRENCY}
      showValidationErrors={showValidationErrors}
      markers={markers}
      dynamicPortfoliosEnabled={
        (instance.flow as any).portfolioCollection?.dynamicPortfoliosEnabled === true
      }
      modelDeviationEnabled={
        (instance.flow as any).portfolioCollection?.allowModelPortfolioRiskDeviation === true
      }
      onSuitabilityBlockerChange={handleSuitabilityBlockerChange}
      onSaveComplete={(refreshHints) => handlePortfolioSaveComplete(goalId, refreshHints)}
    />
  );

  // QL-535 — flows without internal pages (public-only flows) render the
  // minimal header and an empty state, but in the SAME tree as everything
  // else: every header action (Invite client, Cancel advice, Submit, Reopen)
  // needs its dialog mounted, and a second return for the empty state kept
  // dropping them (QP-92 fixed the invite modal, QP-150 reintroduced the early
  // return and lost it again). Only `redirectingTo` below may return early.
  const hasNoInternalPages = !hasInternalPages;

  // QL-264 / QL-737 — brief hand-off state while the browser leaves for the partner system (submit or cancel).
  if (redirectingTo) {
    return (
      <div className="flex items-center justify-center gap-3 py-16 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
        <p>{t.redirectingBack(new URL(redirectingTo).host)}</p>
      </div>
    );
  }

  // QL-399 — processFeedback/reopenDialog are hoisted so they sit next to the
  // header in the single return below (see the QL-535 note above): the old
  // empty-state branch used to show "Reopen flow" with no dialog mounted, and
  // hide the cards while the Process counter still counted them.
  const processFeedback = processState && (
    <FeedbackCardList
      messages={processState.messages}
      language={userLanguage}
      // The instruction only makes sense on a locked flow the viewer can
      // actually reopen — never while already editing, and never when
      // reopening is blocked or the viewer is read-only.
      showReopenInstruction={isCompleted && !isReadOnly && processState.reopen.available}
      onDismiss={(messageId) => dismissMessage.mutate(messageId)}
    />
  );
  const reopenDialog = processState && (
    <ReopenFlowDialog
      open={dialogs.confirmReopen}
      onOpenChange={(open) => {
        setDialog("confirmReopen", open);
        if (!open) setReopenError(null);
      }}
      reopen={processState.reopen}
      isPending={reopenMutation.isPending}
      errorMessage={reopenError}
      onConfirm={(reason) => reopenMutation.mutate(reason)}
    />
  );

  return (
    <div className="space-y-6">
      <WizardHeader
        instanceId={instance.id}
        instanceName={instance.name}
        flowName={instance.flow.name}
        flowDescription={instance.flow.description}
        status={instance.status}
        clientId={clientId}
        isCompleted={isCompleted}
        isReadOnly={isReadOnly}
        isSaving={false}
        isCompleting={isCompleting}
        hasSuitabilityBlockers={hasSuitabilityBlockers}
        hasWithdrawalExceedsValue={hasWithdrawalExceedsValue}
        needsSnapshotRegeneration={instance.needsSnapshotRegeneration}
        snapshotsRegenerating={snapshotsRegenerating}
        submitBlockedByPreview={instance.submitBlockedByPreview}
        canInvite={!!canInvite}
        processState={processState}
        onSaveName={handleSaveName}
        onComplete={handleComplete}
        onCancelAdvice={() => setDialog("confirmCancelAdvice", true)}
        onReopen={() => setDialog("confirmReopen", true)}
        onInviteClick={() => setInviteModalOpen(true)}
        minimal={hasNoInternalPages}
      />

      {processFeedback}
      {reopenDialog}

      {/* QL-535 — the template and snapshot alerts do not depend on internal
          pages: on a public-only flow they are the only thing that explains
          an incomplete Submit preflight (config_error, or the
          instance_expected_value slot from a public-page portfolio
          component), so they render above the page-configuration split. */}
      {/* QL-493 — a template gap has no anchor to highlight, so it is the one
          engine gap shown as text, and always (not after a Submit attempt):
          the advisor cannot fix it and should not discover it at Submit. */}
      {!isCompleted && markers.configErrorReasons.length > 0 && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <p>{t.templateCannotComplete}</p>
            <ul className="mt-2 list-disc pl-5 space-y-1">
              {markers.configErrorReasons.map((reason) => (
                <li key={reason}>{configErrorText(t, reason)}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {/* QL-576 — a follow-up whose relinked accounts would collide on a
          wrapper type: bank data, not a flow component, so there's nothing
          to highlight and the advisor can't fix it inside the flow —
          shown as text, always (same two conditions as the QL-493 alert
          above). */}
      {!isCompleted && markers.followupConflicts.length > 0 && (
        <Alert variant="destructive" data-testid="followup-conflict-alert">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <p>{t.followupConflictTitle}</p>
            <ul className="mt-2 list-disc pl-5 space-y-1">
              {markers.followupConflicts.map((c) => (
                // QL-576 review fix (WEB-1) — the values go straight into the
                // template, so a bank-supplied account name that literally
                // contains placeholder text (e.g. "{accountB}") stays as it is.
                <li key={`${c.goalId}:${c.wrapperType ?? "null"}`}>
                  {t.followupWrapperConflictLine({
                    goal: goals.find((g) => g.id === c.goalId)?.name ?? c.goalId,
                    accountA: c.accounts[0]?.name ?? "?",
                    accountB: c.accounts[1]?.name ?? "?",
                    wrapper: c.wrapperType === null ? t.followupWrapperNone : c.wrapperType,
                  })}
                </li>
              ))}
            </ul>
            <p className="mt-2">{t.followupWrapperConflictRemedy}</p>
          </AlertDescription>
        </Alert>
      )}

      {/* QL-675 — the worker is rebuilding: nothing to click, just wait. */}
      {snapshotsRegenerating && !isCompleted && (
        <Alert variant="info">
          <Loader2 className="h-4 w-4 animate-spin" />
          <AlertDescription>{t.snapshotsRegenerating}</AlertDescription>
        </Alert>
      )}

      {!snapshotsRegenerating &&
        (instance.needsSnapshotRegeneration ||
          (showValidationErrors &&
            (markers.missingSnapshotGoalIds.size > 0 ||
              markers.missingInstanceSnapshotComponents.size > 0 ||
              hasInstanceExpectedValueSnapshotMissing))) &&
        !isCompleted && (
          <Alert variant="destructive">
            <RefreshCw className="h-4 w-4" />
            <AlertDescription className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <span>
                {instance.needsSnapshotRegeneration ? t.snapshotsStale : t.snapshotMissing}
              </span>
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={() => regenerateSnapshotsMutation.mutate()}
                loading={regenerateSnapshotsMutation.isPending}
              >
                {t.refreshSnapshots}
              </Button>
            </AlertDescription>
          </Alert>
        )}

      {hasNoInternalPages ? (
        <EmptyState>{t.noInternalPagesConfigured}</EmptyState>
      ) : (
        <>
          {/* Page navigation with tabs */}
          {instance.flow.multiGoal ? (
            <GoalListWidget
              flowInstanceId={instance.id}
              flowLanguage={flowLanguage}
              goalPages={goalPages}
              portfolioPages={portfolioPages}
              isGoalPageVisible={isGoalPageVisible}
              isPortfolioPageVisible={isPortfolioPageVisible}
              globalPagesBefore={globalPagesBefore}
              globalPagesAfter={globalPagesAfter}
              multiPortfolio={!!instance.flow.multiPortfolio}
              maxGoals={instance.flow.maxGoals ?? FLOW_MAX_GOALS_DEFAULT}
              isCompleted={isLocked}
              goals={goals as unknown as GoalListItem[]}
              showValidationErrors={showValidationErrors}
              markers={markers}
              renderGoalPage={renderGoalPage}
              renderPortfolioPage={renderPortfolioPage}
              renderGlobalPage={renderGlobalPage}
            />
          ) : goals.length > 0 ? (
            // Single-goal — the goal's context (goal pages + portfolios) hoists into one flat row
            // with the global pages hosted alongside.
            <FlowNavigationProvider onGoalChange={() => {}}>
              <AdvisorGoalContext
                goalId={goals[0].id}
                instanceId={instance.id}
                isCompleted={isLocked}
                multiPortfolio={!!instance.flow.multiPortfolio}
                goalPages={goalPages}
                portfolioPages={portfolioPages}
                isGoalPageVisible={isGoalPageVisible}
                isPortfolioPageVisible={isPortfolioPageVisible}
                renderGoalPage={renderGoalPage}
                renderPortfolioPage={renderPortfolioPage}
                globalPagesBefore={globalPagesBefore}
                globalPagesAfter={globalPagesAfter}
                renderGlobalPage={renderGlobalPage}
                showValidationErrors={showValidationErrors}
                markers={markers}
              />
            </FlowNavigationProvider>
          ) : (
            <Card>
              <CardContent className="pt-6">
                <div className="text-sm text-muted-foreground">{t.loadingGoalInfo}</div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* QL-535 — dialogs for the header actions, mounted for every page
          configuration (see hasNoInternalPages above). */}
      <ConfirmDialog
        open={confirmSubmitOpen}
        onOpenChange={(open) => setDialog("confirmSubmit", open)}
        title={t.submitFlow}
        description={t.submitConfirmDescription}
        confirmText={t.submit}
        cancelText={t.cancel}
        onConfirm={handleCompleteConfirm}
        variant="default"
      />

      <Dialog
        open={dialogs.confirmCancelAdvice}
        onOpenChange={(open) => {
          setDialog("confirmCancelAdvice", open);
          if (!open) setCancelReason("");
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.cancelAdviceTitle}</DialogTitle>
            <DialogDescription>{t.cancelAdviceDescription}</DialogDescription>
          </DialogHeader>
          {cancelReasonMode !== "hidden" && (
            <div className="space-y-2">
              <Label htmlFor="cancel-advice-reason">
                {t.cancelReasonLabel}
                {cancelReasonMode === "required" && <span className="text-destructive"> *</span>}
              </Label>
              <Textarea
                id="cancel-advice-reason"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder={t.cancelReasonPlaceholder}
                rows={4}
              />
            </div>
          )}
          <DialogFooter>
            <DialogCancel>{t.cancel}</DialogCancel>
            <Button
              variant="destructive"
              disabled={cancelReasonMode === "required" && !cancelReason.trim()}
              onClick={() => {
                setDialog("confirmCancelAdvice", false);
                cancelAdviceMutation.mutate(cancelReason.trim() || null);
                setCancelReason("");
              }}
            >
              {t.cancelAdviceConfirm}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertMessage
        open={alertOpen}
        onOpenChange={(open) => {
          if (!open) clearAlert();
        }}
        title={alertConfig.title}
        description={alertConfig.description}
        variant={alertConfig.variant}
      />

      {canInvite && instance.client && (
        <InviteClientModal
          open={inviteModalOpen}
          onClose={() => setInviteModalOpen(false)}
          flowInstanceId={instance.id}
          clientId={instance.client.id}
          clientName={instance.client.name}
          clientEmail={instance.client.email}
          clientMobilePhone={instance.client.mobilePhone}
          publicToken={instance.publicToken}
          publicAccessRevokedReason={instance.publicAccessRevokedReason}
          publicTokenExpiry={instance.publicTokenExpiry}
          onSuccess={() => refresh()}
        />
      )}
    </div>
  );
}
